import React, { useEffect } from "react";
import { serverCall } from "../../../../../redux/calls/serverCall";
import { returnPageImage } from "../index";
import { NumberFormat as formatNumber } from "../../../../../utils/numberFormat.js";
import DosPageSkelton from "../../skeltons/dosPageSketon";
import FieldRenderer from "./fieldRenderer.jsx";
import { v4 as uuidv4 } from "uuid";

const CanvasRenderer = ({
  classes,
  formData,
  activePage,
  canvasRefs,
  bindFieldsCount,
  setbindFieldsCount,
  isRendered,
  formValues,
  droppedFields,
  setDroppedFields,
}) => {
  // API change handlers
  const handleInputChange = async (e) => {
    let newValue = e.target.value;
    const cleaned = newValue.replace(/,/g, "");
    let payload = {};
    if (!isNaN(Number(cleaned)) && cleaned.trim() !== "") {
      payload = { value: cleaned };
    } else {
      payload = { value: newValue };
    }

    const url = `${process.env.REACT_APP_BASE_URL}api/docs/folders/${formData.id}/forms/${formData?.id}/field/`;
    if (newValue !== "") {
      await serverCall("put", url + e.target.id, payload);
    }
  };

  const handleCheckboxChange = async (e) => {
    const url = `${process.env.REACT_APP_BASE_URL}api/docs/folders/${formData.id}/forms/${formData?.id}/field/`;
    const payload = { value: `${e.target.checked}` };
    await serverCall("put", url + e.target.id, payload);
  };

  const handleRadioChange = async (e) => {
    const url = `${process.env.REACT_APP_BASE_URL}api/docs/folders/${formData.id}/forms/${formData?.id}/field/`;
    const payload = { value: `${e.target.value}` };
    await serverCall("put", url + e.target.fieldId, payload);
  };

  // Canvas and field rendering
  const addFields = (data, index) => {
    if (formData?.forms_data?.file) {
      const pdfDoc = returnPageImage(formData?.forms_data?.file, index);
      var canvas = document.getElementById(`myCanvas${index}`);
      canvas.style.width = "100%";
      var ctx = canvas.getContext("2d");
      loadImage(pdfDoc, canvas, ctx, index, data).then((count) => {
        setbindFieldsCount(count);
      });
    }
  };

  const loadImage = (src, canvas, ctx, index, data) =>
    new Promise((resolve, reject) => {
      let img = new Image();
      img.onload = () => {
        canvas.height = img.height;
        canvas.width = img.width;
        ctx.drawImage(img, 0, 0);
        if (data?.form_fields?.length) {
          Promise.all(
            data.form_fields.map((field) => {
              addInput(
                field,
                canvas.offsetLeft,
                canvas.offsetTop + canvas.height,
                index
              );
            })
          ).then(() => {
            resolve(data.form_fields.length);
          });
        }
      };
      img.onerror = reject;
      img.src = src;
    });

  const addInput = (field, offsetWidth, offsetHeight, index) => {
    let fieldVal = field?.field;
    let element;
    let xCo = parseFloat(fieldVal?.x_pos_per);
    let yCo = parseFloat(fieldVal?.y_pos_per);
    let width = parseFloat((fieldVal?.width / 612) * 100);
    let height = parseFloat((fieldVal?.height / 792) * 100);

    // Determine the element type based on field type
    switch (fieldVal?.type) {
      case "date":
        element = document.createElement("input");
        element.type = "date";
        element.value = field?.value || "";
        break;

      case "checkbox-list":
        element = document.createElement("input");
        element.type = "checkbox";
        if (field?.checked) {
          element.checked = field?.checked;
        } else if (field?.value === "true") {
          element.checked = true;
        } else {
          element.checked = false;
        }
        element.style.transform = "scale(1.5)";
        break;

      case "radio":
        // For radio type, we handle it differently
        // Skip creating the main container here, as we'll create individual positioned options

        // Add radio options from the options array
        if (fieldVal?.options && Array.isArray(fieldVal.options)) {
          const radioGroupName = `radio_group_${field?.id}`;
          const fieldWrapper = document.getElementById(`fieldWrap${index}`);

          // First, add the group label if exists (positioned at the top of all radio options)
          if (fieldVal?.label) {
            const headingElement = document.createElement("div");
            headingElement.textContent = fieldVal.label;
            headingElement.style.fontWeight = "bold";
            headingElement.style.fontSize = "1.1vw";
            headingElement.style.position = "absolute";

            // If there's at least one option with positioning, use it for the header
            if (
              fieldVal.options[0]?.x_pos_per &&
              fieldVal.options[0]?.y_pos_per
            ) {
              const xCoHeader = parseFloat(fieldVal.options[0].x_pos_per);
              // Position the header slightly above the first option
              const yCoHeader = parseFloat(fieldVal.options[0].y_pos_per) + 5;
              headingElement.style.left = xCoHeader + "%";
              headingElement.style.bottom = yCoHeader + "%";
            } else {
              // Fallback if no positioning in options
              headingElement.style.left = xCo + "%";
              headingElement.style.bottom = yCo + 5 + "%";
            }

            // headingElement.style.backgroundColor = "#ccd7fd";
            headingElement.style.padding = "2px 8px";
            headingElement.style.borderRadius = "4px";
            fieldWrapper?.appendChild(headingElement);
          }

          // Then add each radio option with its own positioning
          fieldVal.options.forEach((option, optionIndex) => {
            const radioContainer = document.createElement("div");
            radioContainer.style.display = "flex";
            radioContainer.style.alignItems = "center";

            // Get position for this specific option
            const xCoOption = parseFloat(
              option.x_pos_per || fieldVal?.x_pos_per
            );
            const yCoOption = parseFloat(
              option.y_pos_per || fieldVal?.y_pos_per
            );

            // Position the option container absolutely
            radioContainer.style.position = "absolute";
            radioContainer.style.left = xCoOption + "%";
            radioContainer.style.bottom = yCoOption + "%";
            // radioContainer.style.backgroundColor = "#ccd7fd";
            // radioContainer.style.padding = "4px";
            // radioContainer.style.borderRadius = "4px";
            radioContainer.title = option.caption;

            const radioInput = document.createElement("input");
            radioInput.type = "radio";
            radioInput.name = radioGroupName;
            radioInput.id = `${field?.id}_option_${optionIndex}`;
            radioInput.fieldId = `${field?.id}`;
            radioInput.value = option.value || option;
            radioInput.style.marginRight = "5px";
            radioInput.style.transform = "scale(1.2)";
            radioInput.title = option.caption;
            // Check if this option is selected
            if (field?.value === option.value) {
              radioInput.checked = true;
            }

            // const radioLabel = document.createElement("label");
            // radioLabel.htmlFor = `${field?.id}_option_${optionIndex}`;
            // radioLabel.textContent = option.caption || option;
            // radioLabel.style.fontSize = "0.9vw";
            // radioLabel.style.marginLeft = "5px";

            radioContainer.appendChild(radioInput);
            // radioContainer.appendChild(radioLabel);
            fieldWrapper?.appendChild(radioContainer);
          });

          // Since we directly added elements to the fieldWrapper, return to avoid appending anything else
          return;
        } else {
          // Fallback for when options aren't provided
          element = document.createElement("input");
          element.type = "radio";
          element.checked = field?.checked || false;
          element.style.transform = "scale(1.5)";
        }
        break;

      case "string":
        element = document.createElement("input");
        element.type = "text";

        if (fieldVal.caption?.includes("Amount")) {
          element.value = formatNumber({
            number: field?.value || "0",
            maximumFractionDigits: 2,
          });
          element.addEventListener("blur", (e) => {
            element.value = formatNumber({
              number: e.target.value.replace(/,/g, "") || "0",
              maximumFractionDigits: 2,
            });
          });
        } else {
          element.value = field?.value || "";
        }
        break;

      case "number":
        element = document.createElement("input");
        element.type = "text";

        if (fieldVal.caption?.includes("Amount")) {
          element.value = formatNumber({
            number: field?.value || "0",
            maximumFractionDigits: 2,
          });
          element.addEventListener("blur", (e) => {
            element.value = formatNumber({
              number: e.target.value.replace(/,/g, "") || "0",
              maximumFractionDigits: 2,
            });
          });
        } else {
          element.value = field?.value || "";
        }
        break;

      case "clause":
        element = document.createElement("textarea");
        element.value = field?.value || "";
        break;

      case "people":
        element = document.createElement("input");
        element.type = "text";
        // element.placeholder = "Enter name";
        element.value = field?.value || "";
        break;

      case "location":
        element = document.createElement("input");
        element.type = "text";
        // element.placeholder = "Enter location";
        element.value = field?.value || "";
        break;

      case "telephone":
        element = document.createElement("input");
        element.type = "tel";
        // element.placeholder = "Enter phone number";
        element.value = field?.value || "";
        break;

      case "signature":
        element = document.createElement("div");
        element.style.display = "flex";
        element.style.justifyContent = "center";
        element.innerHTML =
          fieldVal?.value || field?.value
            ? `<img src="${
                fieldVal?.value || field?.value
              }" alt="Signature" style="max-width: 100%; max-height: 100%;">`
            : '<div style="display: flex; align-items: center; justify-content: center; height: 100%; border: 1px solid rgba(180,180,180,0.2);border-radius:30px;width:130px;background-color:rgba(233, 233, 233, 0.7)">Signature</div>';
        break;

      case "logo":
        element = document.createElement("div");
        element.style.display = "flex";
        element.style.justifyContent = "center";
        element.style.alignItems = "center";
        element.innerHTML =
          fieldVal?.value || field?.value
            ? `<img src="${
                fieldVal?.value || field?.value
              }" alt="logo" style="max-width: 100%; max-height: 100%;object-fit:contain">`
            : '<div style="display: flex; align-items: center; justify-content: center; height: 100%; border: 1px solid rgba(180,180,180,0.2);background-color:rgba(233, 233, 233, 0.7);">Logo</div>';
        break;

      default:
        element = document.createElement("input");
        element.type = "text";
        break;
    }

    // Only continue with common element properties if we didn't return early (for radio type)
    if (element) {
      // Set common attributes
      element.name = fieldVal?.name;
      element.title = fieldVal?.instructions;
      element.id = field?.id;

      // Position the main element absolutely
      element.style.position = "absolute";
      element.style.left = xCo + "%";
      element.style.bottom = yCo + "%";
      element.style.fontSize = "17px";
      if (fieldVal?.type !== "signature" && fieldVal?.type !== "logo") {
        element.style.backgroundColor = "rgba(0,0,0,.05)";
      }

      element.style.border = "0";

      // Set width and height for applicable elements
      if (fieldVal?.type !== "checkbox-list" && fieldVal?.type !== "radio") {
        element.style.width = width + "%";
        element.style.height = height + "%";
      } else {
        // For checkbox-list types, set container dimensions
        element.style.width = width + "%";
        element.style.height = "auto";
        element.style.maxHeight = height + "%";
        element.style.overflow = "auto";
        element.style.padding = "8px";
        element.style.boxSizing = "border-box";
      }

      // Additional styling for specific types
      if (fieldVal?.type === "clause") {
        element.style.resize = "none";
        element.style.overflow = "auto";
      }

      // Append the element to the field wrapper
      document.getElementById(`fieldWrap${index}`)?.appendChild(element);

      // Additional setup for signature field if needed
      if (fieldVal?.type === "signature" && !field?.value) {
        // You might want to initialize a signature pad library here
        // This is just a placeholder for where you would add that code
      }
      // Text alignment and focus handling for input fields
      if (fieldVal?.type === "date") {
        element.style.textAlign = "center";
      } else if (
        (fieldVal?.type === "string" ||
          fieldVal?.type === "number" ||
          fieldVal?.type === "people" ||
          fieldVal?.type === "location" ||
          fieldVal?.type === "telephone") &&
        !fieldVal.instructions?.includes("dollar") &&
        !fieldVal.caption?.includes("Amount")
      ) {
        element.style.textAlign = "center";

        element.addEventListener("focus", () => {
          element.style.textAlign = "left";
        });

        element.addEventListener("blur", () => {
          element.style.textAlign = "center";
        });
      }
      // Set background and hover effects for non-custom elements
      if (
        fieldVal?.type !== "checkbox-list" &&
        fieldVal?.type !== "radio" &&
        fieldVal?.type !== "signature" &&
        fieldVal?.type !== "logo"
      ) {
        element.style.backgroundColor = "rgba(0,0,0,.05)";

        element.addEventListener("mouseenter", () => {
          element.style.backgroundColor = "#cadbee";
        });

        element.addEventListener("mouseleave", () => {
          element.style.backgroundColor = "rgba(0,0,0,.05)";
        });
      }
    }
  };
  // Handle drop functionality
  const handleDrop = (e, pageIndex) => {
    e.preventDefault();

    try {
      const transferData = e.dataTransfer.getData("text/plain");

      // Try to parse as JSON first (new field)
      let dragData;
      let isNewField = false;

      try {
        dragData = JSON.parse(transferData);
        if (dragData && dragData.tabType && dragData.user) {
          isNewField = true;
        }
      } catch (parseError) {
        // If parsing fails, treat as existing field ID
        dragData = transferData;
      }

      // Handle repositioning existing field (only if it's a simple field ID and element exists)
      if (!isNewField && dragData && document.getElementById(dragData)) {
        const fieldId = dragData;
        const element = document.getElementById(fieldId);
        const canvas = document.getElementById(`myCanvas${pageIndex}`);
        if (!canvas) return;

        // Check if the field is being moved to the same page
        const existingField = droppedFields.find(
          (field) => field.id === fieldId
        );
        if (existingField && existingField.pageIndex === pageIndex) {
          // Same page - just reposition
          const canvasRect = canvas.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const elementWidth = elementRect.width;
          const elementHeight = elementRect.height;

          const cursorXInCanvas = e.clientX - canvasRect.left;
          const cursorYInCanvas = e.clientY - canvasRect.top;

          const xPos = (cursorXInCanvas / canvasRect.width) * 100;
          const yPos =
            ((canvasRect.height - cursorYInCanvas - elementHeight) /
              canvasRect.height) *
            100;

          const clampedXPos = Math.max(0, Math.min(100, xPos));
          const clampedYPos = Math.max(0, Math.min(100, yPos));

          element.style.left = `${clampedXPos}%`;
          element.style.bottom = `${clampedYPos}%`;

          setDroppedFields((prev) => {
            return prev.map((item) => {
              if (item.id === fieldId) {
                return {
                  ...item,
                  pageIndex,
                  xPos: clampedXPos,
                  yPos: clampedYPos,
                };
              }
              return item;
            });
          });

          return;
        } else if (existingField && existingField.pageIndex !== pageIndex) {
          // Different page - create new field and remove old one
          const canvas = document.getElementById(`myCanvas${pageIndex}`);
          if (!canvas) return;

          const canvasRect = canvas.getBoundingClientRect();
          const cursorXInCanvas = e.clientX - canvasRect.left;
          const cursorYInCanvas = e.clientY - canvasRect.top;

          const elementWidth = existingField.width || 100;
          const elementHeight = existingField.height || 36;

          const xPos = (cursorXInCanvas / canvasRect.width) * 100;
          const yPos =
            ((canvasRect.height - cursorYInCanvas - elementHeight) /
              canvasRect.height) *
            100;

          const clampedXPos = Math.max(0, Math.min(100, xPos));
          const clampedYPos = Math.max(0, Math.min(100, yPos));

          // Create new field with same properties but new position and page
          const newField = {
            ...existingField,
            id: uuidv4(), // Generate new ID
            pageIndex,
            xPos: clampedXPos,
            yPos: clampedYPos,
            timestamp: new Date().toISOString(),
          };

          // Remove the original DOM element from the canvas
          const originalElement = document.getElementById(fieldId);
          if (originalElement && originalElement.parentNode) {
            originalElement.parentNode.removeChild(originalElement);
          }

          // Update state: remove old field and add new one
          setDroppedFields((prev) => {
            return prev.filter((item) => item.id !== fieldId).concat(newField);
          });

          return;
        }
      }

      // Handle new field drop
      if (isNewField) {
        try {
          const canvas = document.getElementById(`myCanvas${pageIndex}`);
          if (!canvas) return;

          const canvasRect = canvas.getBoundingClientRect();
          const floatingElement = document.getElementById(
            "floating-drag-element"
          );
          let elementWidth = 100;
          let elementHeight = 36;

          if (floatingElement) {
            const floatingRect = floatingElement.getBoundingClientRect();
            elementWidth = floatingRect.width;
            elementHeight = floatingRect.height;
          }

          const cursorXInCanvas = e.clientX - canvasRect.left;
          const cursorYInCanvas = e.clientY - canvasRect.top;

          const xPos = (cursorXInCanvas / canvasRect.width) * 100;
          const yPos =
            ((canvasRect.height - cursorYInCanvas - elementHeight) /
              canvasRect.height) *
            100;

          const clampedXPos = Math.max(0, Math.min(100, xPos));
          const clampedYPos = Math.max(0, Math.min(100, yPos));

          const newField = {
            id: uuidv4(),
            pageIndex,
            tabType: dragData.tabType,
            user: dragData.user,
            xPos: clampedXPos,
            yPos: clampedYPos,
            width: dragData.width || 100,
            height: dragData.height || 36,
            value: "",
            isSelected: false,
            color: dragData.color || "#e6eaff",
            borderColor: dragData.borderColor || "#ff9999",
            textColor: dragData.textColor || "#2868d7",
            borderWidth: dragData.borderWidth || 1,
            borderStyle: dragData.borderStyle || "solid",
            timestamp: new Date().toISOString(),
          };

          setDroppedFields((prev) => [...prev, newField]);

          if (floatingElement) {
            document.body.removeChild(floatingElement);
          }
        } catch (error) {
          console.error("Error creating new field:", error);
        }
      }
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };
  // Effect to add event listeners
  useEffect(() => {
    if (bindFieldsCount !== 0) {
      document.querySelectorAll("input").forEach((el) => {
        if (["text", "date", "number", "tel"].includes(el.type)) {
          el.addEventListener("blur", handleInputChange);
        } else if (el.type === "checkbox") {
          el.addEventListener("change", handleCheckboxChange);
        } else if (el.type === "radio") {
          el.addEventListener("change", handleRadioChange);
        }
      });

      return () => {
        document.querySelectorAll("input").forEach((el) => {
          if (["text", "date", "number", "tel"].includes(el.type)) {
            el.removeEventListener("blur", handleInputChange);
          } else if (el.type === "checkbox") {
            el.removeEventListener("change", handleCheckboxChange);
          } else if (el.type === "radio") {
            el.removeEventListener("change", handleRadioChange);
          }
        });
      };
    }
  }, [bindFieldsCount, formValues]);

  // Effect to render fields when form data changes
  useEffect(() => {
    if (formData?.forms_data?.total_pages && !isRendered?.current) {
      isRendered.current = true;
      Array.from(Array(formData?.forms_data?.total_pages || 0).keys())?.map(
        (item, index) => {
          addFields(
            {
              ...formData,
              form_fields: formData?.form_fields?.filter(
                (it) => it.field?.page_number === index
              ),
            },
            index
          );
        }
      );
    }
  }, [formData?.id, formData?.forms_data?.total_pages]);

  // Cleanup on form change
  useEffect(() => {
    Array.from(Array(formData?.form_fields?.length).keys()).map((item) => {
      const myNode = document.getElementById(`fieldWrap${item}`);
      if (myNode) {
        myNode.innerHTML = "";
      }
    });
    isRendered.current = false;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [formData?.form_id]);

  return (
    <>
      {Array.from(Array(formData?.forms_data?.total_pages).keys())?.length ? (
        Array.from(Array(formData?.forms_data?.total_pages).keys())?.map(
          (item, index) => (
            <div
              className={classes.mPosition}
              style={{
                boxShadow: activePage == index ? "-4px 0 0 0 #2868D7" : "",
              }}
              key={index}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, index)}
            >
              <canvas
                id={`myCanvas${index}`}
                className={classes.mCanvas}
                ref={(el) => (canvasRefs.current[index] = el)}
              ></canvas>
              <div
                id={`fieldWrap${index}`}
                className={classes.mColor}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, index)}
              ></div>
            </div>
          )
        )
      ) : (
        <DosPageSkelton />
      )}

      <FieldRenderer
        droppedFields={droppedFields}
        setDroppedFields={setDroppedFields}
        activePage={activePage}
      />
    </>
  );
};

export default CanvasRenderer;
